import gql from 'graphql-tag';
import isEqual from 'lodash/isEqual';
import orderBy from 'lodash/orderBy';
import flattenDeep from 'lodash/flatMapDeep';
import NotificationService from '@/services/NotificationService';
import StorageService from '@/services/StorageService';
import ErrorHandler from '@/services/ErrorHandler';
import { apolloClient } from '@/apollo';

const selectionsDishesIdsString = (selections) => {
  const result = flattenDeep(selections.map(s => s.dishes.map(d => d.id))).sort().join('');

  return result;
};

const categoryNotSelected = (activeSelections, newSelection) => {
  if (activeSelections.providerId === newSelection.providerId) {
    return activeSelections.categoryId !== newSelection.categoryId;
  }

  const newSelectionIsSoup = newSelection.dishes.some(sd => sd.isSoup);

  return activeSelections.dishes.some(d => d.isSoup !== newSelectionIsSoup);
};

export const actions = {
  resetCategories({ commit }) {
    commit('resetCategories');
  },
  setCategories({ commit }, categories) {
    commit('setCategories', categories || []);
  },
  selectCategory({ commit }, category) {
    commit('selectCategory', category);
  },
  addSelection({ commit }, { selection, isSecondary }) {
    commit('addSelection', { selection, isSecondary });
  },
  removeSelection({ commit }, selection) {
    commit('removeSelection', selection);
  },
  reloadSelections({ commit }, selections) {
    commit('reload', selections);
  },
  savePreviousSelection(_, dayOfWeek) {
    this.dispatch('dishesStore/saveSelection', { dayOfWeek, savePrevious: true });
  },
  async saveSelection({ commit, state }, { dayOfWeek, savePrevious }) {
    try {
      commit('updateState', 'loading');
      const selectionsToSave = savePrevious ? state.previousSelections : state.activeSelections;
      const requestDishIds = flattenDeep(selectionsToSave.map(s => s.dishes.map(d => ({ dishId: d.id, isSecondary: s.isSecondary }))));
      await apolloClient.mutate({
        mutation: gql`mutation SaveUserSelectionsMutation($dayOfWeek: DayOfWeek!, $requestDishIds: [RequestDishIdInput]!){
          saveDishes(requestDishIds: $requestDishIds, dayOfWeek: $dayOfWeek)
        }`,
        variables: {
          requestDishIds,
          dayOfWeek,
        },
      });

      await this.dispatch('userInfoStore/refetchUserBalance');
      state.confirmedSelections = [...state.activeSelections];
      state.nonConfirmedSelections = [];
      commit('updateState', 'done');
      state.noSelectionMadeYet = false;
      this.dispatch('dishesStore/sendSuccessMessage', { dayOfWeek, savePrevious: true });
    } catch (error) {
      commit('updateState', 'dirty');
      ErrorHandler.handleApolloError(error);
    }
  },
  sendSuccessMessage({ state }) {
    const disableFancyMessages = StorageService.get('disableFancyMessages') === 'true';
    NotificationService.showSnackbar({
      message: state.onSavedMessages[disableFancyMessages ? 0 : Math.floor(Math.random() * state.onSavedMessages.length)],
      timeout: 4000,
      position: 'top',
    });
  },
};

export const getters = {
  activeSelections(state) {
    return state.activeSelections;
  },
  confirmedSelections(state) {
    return state.confirmedSelections;
  },
  nonConfirmedSelections(state) {
    return state.nonConfirmedSelections;
  },
  activeSelectionsState(state) {
    return state.activeSelectionsState;
  },
  selectedCategory(state) {
    return state.selectedCategory;
  },
  categories(state) {
    return state.categories;
  },
  noSelectionMadeYet(state) {
    return state.noSelectionMadeYet;
  },
};

export const mutations = {
  resetCategories(state) {
    state.selectedCategory = { selections: [] };
    state.categories = [];
  },
  setCategories(state, categories) {
    const sortedCategories = orderBy(categories, c => (c.selections.find(s => s.dishes[0].isSoup)), 'asc');
    if (!isEqual(state.categories.map(c => c.id), categories.map(c => c.id))) {
      [state.selectedCategory] = sortedCategories;
    }
    state.categories = sortedCategories;
  },
  reload(state, selections) {
    state.previousSelections = state.activeSelections;
    state.activeSelections = [...selections || []];
    state.confirmedSelections = [...selections || []];
    state.nonConfirmedSelections = [];
    state.noSelectionMadeYet = !selections;
    if (state.noSelectionMadeYet) {
      state.activeSelectionsState = 'dirty';
    } else {
      state.activeSelectionsState = 'done';
    }
  },
  updateState(state, newState) {
    state.activeSelectionsState = newState;
  },
  selectCategory(state, category) {
    state.selectedCategory = category;
  },
  removeSelection(state, selection) {
    const indexToRemoveActiveSelection = state.activeSelections.findIndex(s => s.id === selection.id && (s.isSecondary || !s.isSecondary));
    const indexToRemoveNonConfirmedSelection = state.nonConfirmedSelections.findIndex(s => s.id === selection.id && (s.isSecondary || !s.isSecondary));

    state.activeSelections.splice(indexToRemoveActiveSelection, 1);
    if (indexToRemoveNonConfirmedSelection !== -1) {
      state.nonConfirmedSelections.splice(indexToRemoveNonConfirmedSelection, 1);
    }

    state.activeSelectionsState = 'dirty';

    if (selectionsDishesIdsString(state.confirmedSelections) === selectionsDishesIdsString(state.activeSelections)) {
      state.activeSelectionsState = 'done';
    }
  },
  addSelection(state, { selection, isSecondary }) {
    if (state.activeSelections.length >= 10) {
      NotificationService.showSnackbar({
        message: 'dishesContainer.noMoreDishes',
        position: 'top',
      });
      return;
    }

    if (!isSecondary) {
      state.activeSelections = state.activeSelections.filter(as => categoryNotSelected(as, selection) || as.isSecondary);
      state.nonConfirmedSelections = state.nonConfirmedSelections.filter(ncs => categoryNotSelected(ncs, selection) || ncs.isSecondary);
    }

    state.activeSelectionsState = 'dirty';
    state.activeSelections.push(Object.assign({}, selection, { isSecondary }));
    state.nonConfirmedSelections.push(Object.assign({}, selection, { isSecondary }));
    state.noSelectionMadeYet = false;

    if (selectionsDishesIdsString(state.confirmedSelections) === selectionsDishesIdsString(state.activeSelections)) {
      state.nonConfirmedSelections = [];
      state.activeSelectionsState = 'done';
    }
  },
};

export const state = {
  /*eslint-disable */
  selectedCategory: { selections: [] },
  categories: [],
  noSelectionMadeYet: false,
  activeSelections: [],
  previousSelections: [],
  confirmedSelections: [],
  nonConfirmedSelections: [],
  activeSelectionsState: 'done',
  onSavedMessages:  ["Your order was saved!", "Good Choice!", "Cha-ching!", "I love to eat 😜", "Shazam 😎!", "Can't wait to eat it 🍲!", "That was a hard choice!", "Hallelujah!", "Ta da!", "That's gonna be good 🤩!", "Food is life 🌯", "Good stuff", "Nice choice", "POW 💥", "…And boom goes the dynamite. 💥", "Shazzzamm!", 'Whoop. There it is.', 'Cha-Ching.', 'Eating = Living']
};

export const DishesStore = {
  namespaced: true,
  state,
  actions,
  mutations,
  getters
};
